import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search } from "lucide-react";

const ROLE = { admin: "Admin", top_management: "Top Management", employee: "Employee" };

const BOARD = {
  obligations: { title: "Share Obligations board", what: "your Team board live (Month, Year, Forecast, all entities and amounts)",
    levels: [["view", "View only"], ["pay", "Can mark paid"]], hint: "view only, or also mark payments paid" },
  filing: { title: "Share Filing board", what: "your team's Filing board live (all clients and filings, month by month)",
    levels: [["view", "View only"], ["done", "Can mark done"], ["invoice", "Mark done + invoice"]], hint: "view only, mark filings done (with acknowledgement), or also set invoicing" },
  vault: { title: "Share Vault board", what: "the File Vault board live (every physical file, its shelf, document index and current holder)",
    levels: [["view", "View only"], ["issue", "Can issue & return"]], hint: "view only, or also issue files out and receive them back" },
};

export const ShareBoardDialog = ({ open, onClose, board = "obligations" }) => {
  const cfg = BOARD[board];
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Map());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setData(null); setQ("");
    api.get("/obligations-board/shares", { params: { board } }).then((r) => { setData(r.data); setSel(new Map(r.data.shares.map((s) => [s.user_id, s.access]))); }).catch((e) => toast.error(formatErr(e)));
  }, [open, board]);
  const list = useMemo(() => (data?.candidates || []).filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const toggle = (id) => setSel((s) => { const n = new Map(s); if (n.has(id)) n.delete(id); else n.set(id, "view"); return n; });
  const setAccess = (id, a) => setSel((s) => new Map(s).set(id, a));
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.put(`/obligations-board/shares?board=${board}`, { shares: [...sel].map(([user_id, access]) => ({ user_id, access })) });
      toast.success(r.shares.length ? `Board shared with ${r.shares.length} user(s)${r.emailed ? ` · ${r.emailed} email(s) sent` : ""}` : "Board is no longer shared");
      onClose();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="share-board-dialog">
        <DialogHeader>
          <DialogTitle>{cfg.title}</DialogTitle>
          <DialogDescription>Picked users see {cfg.what}. {cfg.note || `Choose per person: ${cfg.hint}. They can't edit or delete records.`} People added or whose access changes get an email.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="share-board-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" className="pl-8" />
        </div>
        <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="share-board-list">
          {!data ? <div className="p-4 text-sm text-slate-500">Loading…</div> : list.map((u) => (
            <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-blue-50/40" data-testid={`share-board-user-${u.id}`}>
              <input type="checkbox" className="accent-blue-700 w-4 h-4" checked={sel.has(u.id)} onChange={() => toggle(u.id)} data-testid={`share-board-check-${u.id}`} />
              <span className="flex-1 min-w-0"><span className="block text-sm text-slate-900 truncate">{u.name || u.email}</span><span className="block text-[11px] text-slate-500 truncate">{u.email}</span></span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{ROLE[u.role] || u.role}</span>
              {sel.has(u.id) && cfg.levels.length === 1 && <span className="text-xs text-blue-700" data-testid={`share-board-access-${u.id}`}>{cfg.levels[0][1]}</span>}
              {sel.has(u.id) && cfg.levels.length > 1 && (
                <select data-testid={`share-board-access-${u.id}`} value={sel.get(u.id)} onChange={(e) => setAccess(u.id, e.target.value)}
                  className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-700">
                  {cfg.levels.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
            </label>
          ))}
          {data && list.length === 0 && <div className="p-4 text-sm text-slate-500">{q ? "No one matches." : "No one else in your organisation yet."}</div>}
        </div>
        <DialogFooter className="items-center">
          <span className="text-xs text-slate-500 mr-auto" data-testid="share-board-count">{sel.size} selected</span>
          <Button variant="outline" onClick={onClose} data-testid="share-board-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !data} data-testid="share-board-save" className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
